/**
 * Suivi de facturation par restaurant (Brique 3·D) — LOGIQUE PURE.
 *
 * Règle métier d'Ahmed : l'application N'ENCAISSE PAS. La facturation réelle se
 * fait dans Odoo. Ici on ne fait que SUIVRE un statut par resto, avec souplesse :
 *   - none      : sans facturation (état neutre, ou client retiré)
 *   - free      : gratuit (ex. 20 premiers clients)
 *   - trial     : essai offert N mois (daté via trialEndsAt)
 *   - active    : payant 99€/mois (facturé dans Odoo)
 *   - suspended : suspendu (service à couper)
 *
 * Pur et testable : aucune I/O ici. Le service Supabase est dans billingService.ts.
 */

export type BillingStatus = 'none' | 'free' | 'trial' | 'active' | 'suspended';

/** Tous les statuts, dans l'ordre d'affichage. */
export const BILLING_STATUSES: BillingStatus[] = [
  'none',
  'free',
  'trial',
  'active',
  'suspended',
];

/** Libellés lisibles (FR) pour l'UI. */
export const BILLING_LABELS: Record<BillingStatus, string> = {
  none: 'Sans facturation',
  free: 'Gratuit',
  trial: 'Essai offert',
  active: 'Payant',
  suspended: 'Suspendu',
};

/** Tarif de l'abonnement standard (facturé via Odoo), en euros / mois. */
export const MONTHLY_PRICE_EUR = 99;

/** Durée d'essai par défaut proposée à l'UI (jours). Modifiable au cas par cas. */
export const DEFAULT_TRIAL_DAYS = 30;

/** Données de facturation d'un resto (miroir des colonnes ajoutées sur tenants). */
export interface BillingInfo {
  status: BillingStatus;
  /** Fin d'essai au format ISO (si status = 'trial'), sinon null. */
  trialEndsAt: string | null;
  /** Note libre (ex. « offert 3 mois ouverture »). */
  notes: string | null;
  /** Identifiant de la fiche client Odoo (rapprochement futur), ou null. */
  odooPartnerId: string | null;
}

/** Valeur par défaut sûre quand un tenant n'a pas (encore) d'info de facturation. */
export const EMPTY_BILLING: BillingInfo = {
  status: 'none',
  trialEndsAt: null,
  notes: null,
  odooPartnerId: null,
};

/** Le statut est-il une valeur connue ? (garde-fou contre des données legacy). */
export function isValidBillingStatus(s: unknown): s is BillingStatus {
  return typeof s === 'string' && (BILLING_STATUSES as string[]).includes(s);
}

/**
 * Normalise une ligne tenant (champs potentiellement absents/null) en BillingInfo.
 * Tolérant : un statut inconnu ou manquant retombe sur 'none'.
 */
export function toBillingInfo(row: {
  billing_status?: string | null;
  trial_ends_at?: string | null;
  billing_notes?: string | null;
  odoo_partner_id?: string | null;
}): BillingInfo {
  return {
    status: isValidBillingStatus(row.billing_status) ? row.billing_status : 'none',
    trialEndsAt: row.trial_ends_at ?? null,
    notes: row.billing_notes ?? null,
    odooPartnerId: row.odoo_partner_id ?? null,
  };
}

/**
 * Nombre de jours restants avant la fin de l'essai (arrondi au jour entier).
 * - null si pas de date d'essai.
 * - 0 le dernier jour, négatif une fois l'essai dépassé.
 */
export function daysLeftInTrial(trialEndsAt: string | null, now: Date = new Date()): number | null {
  if (!trialEndsAt) return null;
  const end = new Date(trialEndsAt).getTime();
  if (Number.isNaN(end)) return null;
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.ceil((end - now.getTime()) / msPerDay);
}

/** L'essai est-il dépassé ? (status 'trial' avec une date d'essai passée). */
export function isTrialExpired(info: BillingInfo, now: Date = new Date()): boolean {
  if (info.status !== 'trial' || !info.trialEndsAt) return false;
  const left = daysLeftInTrial(info.trialEndsAt, now);
  return left !== null && left < 0;
}

/**
 * Statut EFFECTIF à un instant donné : un essai dépassé est traité comme 'suspended'
 * (le service doit être coupé tant qu'Ahmed n'a pas basculé le resto en 'active').
 */
export function effectiveBillingStatus(info: BillingInfo, now: Date = new Date()): BillingStatus {
  return isTrialExpired(info, now) ? 'suspended' : info.status;
}

/**
 * Le service (site client) doit-il rester ouvert pour ce resto ?
 * Ouvert pour none / free / trial valide / active. Fermé si suspended ou essai dépassé.
 */
export function isServiceActive(info: BillingInfo, now: Date = new Date()): boolean {
  return effectiveBillingStatus(info, now) !== 'suspended';
}

export type BillingTone = 'good' | 'warn' | 'bad' | 'neutral';

/** Ton d'affichage d'un badge selon le statut effectif (couleur côté UI). */
export function billingTone(info: BillingInfo, now: Date = new Date()): BillingTone {
  const eff = effectiveBillingStatus(info, now);
  if (eff === 'active' || eff === 'free') return 'good';
  if (eff === 'suspended') return 'bad';
  if (eff === 'trial') {
    const left = daysLeftInTrial(info.trialEndsAt, now);
    return left !== null && left <= 7 ? 'warn' : 'neutral'; // essai bientôt fini → alerte douce
  }
  return 'neutral'; // none
}

/**
 * Résumé prêt pour l'affichage : libellé + détail court + ton + accès service.
 * Ex. essai : « Essai offert · 5 j restants ». Payant : « Payant · 99€/mois ».
 */
export function summarizeBilling(info: BillingInfo, now: Date = new Date()): {
  status: BillingStatus;
  effective: BillingStatus;
  label: string;
  detail: string;
  tone: BillingTone;
  served: boolean;
} {
  const effective = effectiveBillingStatus(info, now);
  let detail = '';
  if (info.status === 'trial') {
    const left = daysLeftInTrial(info.trialEndsAt, now);
    if (left === null) detail = 'sans date de fin';
    else if (left < 0) detail = `expiré depuis ${-left} j`;
    else if (left === 0) detail = 'dernier jour';
    else detail = `${left} j restants`;
  } else if (info.status === 'active') {
    detail = `${MONTHLY_PRICE_EUR}€/mois`;
  }
  return {
    status: info.status,
    effective,
    label: BILLING_LABELS[info.status],
    detail,
    tone: billingTone(info, now),
    served: isServiceActive(info, now),
  };
}

/** Date ISO de fin d'essai à J+days à partir de `from` (utilitaire UI « offrir un essai »). */
export function trialEndDate(days: number = DEFAULT_TRIAL_DAYS, from: Date = new Date()): string {
  const d = new Date(from.getTime());
  d.setDate(d.getDate() + days);
  return d.toISOString();
}
